'use client'
import { useState, useEffect, useMemo, startTransition } from 'react'
import { getLocalStorageData, setLocalStorageData } from '@/utils/handleLocalStorage'

import settings from '@/settings/public'
const appCode = settings?.app?.code

type DeviceType = 'mobile' | 'desktop' | 'unknown'
type OSType = 'android' | 'ios' | 'other'

const generateDeviceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return Math.random().toString(36).slice(2)
}

export const useDevice = () => {
  const [deviceId, setDeviceId] = useState<string>('')
  const [deviceType, setDeviceType] = useState<DeviceType>('unknown')
  const [os, setOs] = useState<OSType>('other')

  useEffect(() => {
    if (typeof window === 'undefined' || !appCode) return

    try {
      let currentDeviceId = getLocalStorageData(`_${appCode}:device`)

      if (!currentDeviceId) {
        currentDeviceId = generateDeviceId()
        setLocalStorageData(`_${appCode}:device`, currentDeviceId)
      }

      startTransition(() => setDeviceId(currentDeviceId))

    } catch (error) {
      console.error('Error accessing device ID:', error)
    }

    const detectDevice = () => {
      // UserAgent-based detection
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
      const userAgentLower = userAgent.toLowerCase()

      const isAndroidOS = /android/.test(userAgentLower)
      const isIOSOS = /iphone|ipad|ipod/.test(userAgentLower)

      if (isAndroidOS) {
        startTransition(() => setOs('android'))
      } else if (isIOSOS) {
        startTransition(() => setOs('ios'))
      } else {
        startTransition(() => setOs('other'))
      }

      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
      const isUserAgentMobile = mobileRegex.test(userAgent)

      // Touch-based detection (more robust for tablets/hybrids)
      const hasTouchSupport =
        typeof navigator.maxTouchPoints !== 'undefined' && navigator.maxTouchPoints > 0

      if (isUserAgentMobile || hasTouchSupport) {
        startTransition(() => setDeviceType('mobile'))
      } else {
        startTransition(() => setDeviceType('desktop'))
      }
    }

    detectDevice()
  }, [])

  const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])
  const isAndroid = useMemo(() => os === 'android', [os])
  const isIOS = useMemo(() => os === 'ios', [os])

  return {
    device: deviceId,      // The Unique ID
    deviceType,           // 'mobile' | 'desktop'
    isMobile,             // boolean convenience
    os,                   // 'android' | 'ios' | 'other'
    isAndroid,            // true if Android
    isIOS                 // true if iOS
  }
}